import React, { useState, useEffect } from 'react';
import axios from 'axios';
import API_URL from '../api';
import { useToast } from '../contexts/ToastContext';
import { X, ClipboardList } from 'lucide-react';
import './AssignTaskModal.css';

const AssignTaskModal = ({ isOpen, onClose, student, onSuccess }) => {
  const { success, error: toastError } = useToast();
  const [resourceType, setResourceType] = useState('Quiz');
  const [resources, setResources] = useState([]);
  const [selectedResource, setSelectedResource] = useState('');
  const [loadingResources, setLoadingResources] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    
    const fetchResources = async () => {
      setLoadingResources(true);
      try {
        const token = localStorage.getItem('token');
        const config = { headers: { Authorization: `Bearer ${token}` } };
        let url = '';
        
        if (resourceType === 'Quiz') url = `${API_URL}/quiz`;
        else if (resourceType === 'GameScenario') url = `${API_URL}/game-scenarios`;
        else if (resourceType === 'DeskTemplate') url = `${API_URL}/templates`;
        else if (resourceType === 'ComplexTest') url = `${API_URL}/complex-tests`;
        else if (resourceType === 'PizzaTemplate') url = `${API_URL}/pizza-templates/templates`;

        if (url) {
          const res = await axios.get(url, config);
          setResources(res.data);
        }
      } catch (err) {
        console.error('Error fetching resources:', err);
        toastError('Не вдалося завантажити список ресурсів');
      } finally {
        setLoadingResources(false);
      }
    };

    fetchResources();
    setSelectedResource('');
  }, [resourceType, isOpen, toastError]);

  if (!isOpen || !student) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedResource) {
      toastError('Будь ласка, оберіть ресурс');
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const payload = {
        targetType: 'student',
        resourceType,
        resourceId: selectedResource,
        studentId: student._id || student.studentId
      };

      await axios.post(`${API_URL}/assignments`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const studentName = student.studentName || 'гравця';
      const studentLastName = student.studentLastName || '';
      success(`Завдання успішно призначено для ${studentLastName} ${studentName}!`);
      if (onSuccess) onSuccess();
      onClose();
    } catch (err) {
      console.error('Error creating assignment:', err);
      toastError(err.response?.data?.error || 'Помилка при створенні призначення');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="atm-overlay" onClick={onClose}>
      <div className="atm-modal" onClick={(e) => e.stopPropagation()}>
        <div className="atm-header">
          <div className="atm-title-group">
            <ClipboardList className="atm-title-icon" size={20} />
            <h3>Призначити завдання</h3>
          </div>
          <button className="atm-btn-close" onClick={onClose}><X size={18} /></button>
        </div>
        <form onSubmit={handleSubmit} className="atm-form">
          <div className="atm-student-info">
            Гравець: <strong>{student.studentLastName} {student.studentName}</strong> {student.studentCity && `(${student.studentCity})`}
          </div>
          
          <div className="form-group">
            <label htmlFor="atm-resource-type">Тип ресурсу</label>
            <select
              id="atm-resource-type"
              value={resourceType}
              onChange={(e) => setResourceType(e.target.value)}
            >
              <option value="Quiz">📝 Квіз (Тести)</option>
              <option value="GameScenario">🎮 Гра (Сценарій)</option>
              <option value="DeskTemplate">🍽️ Сервірування столу</option>
              <option value="ComplexTest">📚 Комплексний тест</option>
              <option value="PizzaTemplate">🍕 Конструктор піци</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="atm-resource-select">Оберіть конкретний тест/гру</label>
            {loadingResources ? (
              <div className="atm-loading">Завантаження варіантів...</div>
            ) : (
              <select
                id="atm-resource-select"
                value={selectedResource}
                onChange={(e) => setSelectedResource(e.target.value)}
                required
              >
                <option value="">-- Оберіть зі списку --</option>
                {resources.map((res) => (
                  <option key={res._id} value={res._id}>
                    {res.title || res.name || res.templateName || 'Без назви'}
                  </option>
                ))}
              </select>
            )}
          </div>

          <div className="atm-actions">
            <button type="submit" className="atm-btn-submit" disabled={submitting}>
              {submitting ? 'Призначення...' : 'Призначити'}
            </button>
            <button type="button" className="atm-btn-cancel" onClick={onClose}>
              Скасувати
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AssignTaskModal;
